import { useState, useCallback, useMemo } from 'react';
import { Stack, Group, Text, Button, Divider, Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useNavigate } from '@tanstack/react-router';
import { IconDots, IconTrash, IconShare } from '@tabler/icons-react';
import { PlaylistSettingsForm } from './PlaylistSettingsForm';
import { PlaylistItemsManager } from './PlaylistItemsManager';
import { PlaylistPublishModal } from './PlaylistPublishModal';
import { PlaylistUtils } from '../PlaylistUtils';
import { useUpdateCustomPlaylist, useDeletePlaylists } from '../usePlaylistMutations';
import {
  PlaylistSettingsSchema,
  type PlaylistSettingsFormValues,
} from '../schemas/PlaylistForm.schema';
import { useRefIdConflict } from '@/shared/hooks/useRefIdConflict';
import type { CustomPlaylistView, PlaylistMediaPosition } from '@/lib/types/Playlist';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

interface ManualPlaylistContentProps {
  playlist: CustomPlaylistView;
}

export function ManualPlaylistContent({ playlist }: ManualPlaylistContentProps) {
  const navigate = useNavigate();
  const updateMutation = useUpdateCustomPlaylist(playlist.listId);
  const deleteMutation = useDeletePlaylists();
  const [publishOpened, { open: openPublish, close: closePublish }] = useDisclosure(false);

  // Settings form (RHF)
  const settingsDefaults = PlaylistUtils.toSettingsFormData(playlist);
  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { isDirty: formIsDirty, isSubmitting },
  } = useForm<PlaylistSettingsFormValues>({
    resolver: zodResolver(PlaylistSettingsSchema),
    defaultValues: settingsDefaults,
  });

  const refIdValue = useWatch({ control, name: 'refId' });
  const { conflict, conflictMessage, isChecking } = useRefIdConflict({
    refId: refIdValue,
    originalRefId: playlist.refId ?? '',
    selfScope: 'list',
    selfId: playlist.listId,
  });

  // Media positions (local state, separate from RHF)
  const initialPositions = useMemo(
    () => [...playlist.mediaPositions].sort((a, b) => a.position - b.position),
    [playlist.mediaPositions]
  );
  const [mediaPositions, setMediaPositions] = useState<PlaylistMediaPosition[]>(initialPositions);

  // Deep equality check for positions
  const positionsChanged = useMemo(() => {
    if (mediaPositions.length !== initialPositions.length) return true;
    return mediaPositions.some((p, i) =>
      p.mediaId !== initialPositions[i].mediaId || p.position !== initialPositions[i].position
    );
  }, [mediaPositions, initialPositions]);

  const isDirty = formIsDirty || positionsChanged;

  const onSubmit = useCallback(async (data: PlaylistSettingsFormValues) => {
    // Belt-and-braces for Enter-key submits; the Save button is already
    // disabled while a conflict is shown. Server validation stays authoritative.
    if (conflict) {
      notifications.show({ title: 'Reference ID conflict', message: conflictMessage, color: 'orange' });
      return;
    }
    const request = PlaylistUtils.buildCustomUpdateRequest(data, mediaPositions);

    try {
      const updated = await updateMutation.mutateAsync(request);
      resetForm(data);
      // Reset positions to the updated values from server
      const sortedPositions = [...updated.mediaPositions].sort((a, b) => a.position - b.position);
      setMediaPositions(sortedPositions);
      notifications.show({ title: 'Saved', message: 'Playlist updated.', color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save playlist';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  }, [updateMutation, resetForm, mediaPositions, conflict, conflictMessage]);

  const onInvalid = useCallback((fieldErrors: Record<string, { message?: string } | undefined>) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  }, []);

  const handleDiscard = useCallback(() => {
    resetForm();
    setMediaPositions(initialPositions);
  }, [resetForm, initialPositions]);

  const handleDelete = useCallback(() => {
    modals.openConfirmModal({
      title: 'Delete Playlist',
      children: (
        <Text size="sm">
          Are you sure you want to delete "{playlist.title}"? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate([playlist.listId], {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `"${playlist.title}" deleted.`, color: 'green' });
            navigate({ to: '/playlists' });
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [playlist, deleteMutation, navigate]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid as Parameters<typeof handleSubmit>[1])}>
      <Stack gap="md">
        {/* Page header: playlist-level actions stay always visible; save/discard
            live in the contextual FormSaveBar below */}
        <Group justify="space-between">
          <Text fw={600} size="lg">{playlist.title}</Text>
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconShare size={16} />}
              onClick={openPublish}
              disabled={isDirty}
              title={isDirty ? 'Save changes before publishing' : undefined}
            >
              Publish
            </Button>
            <Menu>
              <Menu.Target>
                <Button variant="subtle" color="gray" px="xs">
                  <IconDots size={16} />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={handleDelete}
                >
                  Delete Playlist
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        <FormSaveBar
          isDirty={isDirty}
          isPending={isSubmitting}
          onDiscard={handleDiscard}
          saveDisabled={conflict != null}
        />

        <PlaylistSettingsForm control={control} refIdConflictError={conflictMessage} refIdChecking={isChecking} />

        <Divider />

        <PlaylistItemsManager
          mediaPositions={mediaPositions}
          onChange={setMediaPositions}
          mediaType={playlist.mediaType}
        />
      </Stack>

      <PlaylistPublishModal
        opened={publishOpened}
        onClose={closePublish}
        playlist={playlist}
      />
    </form>
  );
}
